import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import type * as React from 'react';

import { cn } from '../../../styles/lib/utils';
import { Tooltip } from '../../Tooltip';

const dragHandleVariants = cva(
  'flex items-center select-none rounded-sm cursor-grab active:cursor-grabbing active:bg-blue-10 active:border active:border-blue-50 focus-ring',
  {
    variants: {
      variant: {
        default: 'bg-white text-primary-dark hover:bg-grey-20 border border-transparent hover:bg-grey-20',
        disabled: 'bg-grey-20 text-grey-50 border border-transparent cursor-not-allowed hover:bg-grey-20',
        selected: '!bg-blue-10 text-blue-50 border border-blue-50',
      },
      size: {
        small: 'px-2 py-0.5',
        standard: 'px-2 py-1',
        big: 'px-3 py-2',
        icon: 'p-1',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'standard',
    },
  },
);

function DragHandle({
  className,
  variant,
  size,
  asChild = false,
  tooltip,
  ...dragHandleProps
}: React.ComponentProps<'div'> &
  VariantProps<typeof dragHandleVariants> & {
    asChild?: boolean;
    tooltip?: React.ReactNode;
  }) {
  const Comp = asChild ? Slot : 'div';

  const dragHandleElement = (
    <Comp
      data-slot="drag-handle"
      className={cn(dragHandleVariants({ variant, size, className }))}
      {...dragHandleProps}
    />
  );

  // Only wrap with tooltip if disabled and tooltip is provided
  if (variant === 'disabled' && tooltip) {
    const tooltipProps = typeof tooltip === 'string' ? { text: tooltip } : { contentChildren: tooltip };

    // Wrap in a focusable span to enable pointer events and keyboard focus for tooltip on disabled buttons
    return (
      <Tooltip {...tooltipProps} asChild>
        <span
          className="inline-flex rounded-[6px] focus-ring"
          tabIndex={0}
          role="button"
          aria-disabled="true"
          aria-label={dragHandleProps['aria-label'] ?? 'Drag handle'}
        >
          {dragHandleElement}
        </span>
      </Tooltip>
    );
  }

  return dragHandleElement;
}

export { DragHandle, dragHandleVariants };
